import { useEffect, useRef, useState } from "react";
import Button from "@mui/material/Button";
import { endAgentChat, startAgentChat } from "./ApiService";
import TypingLoader from "./TypingLoader";
import DOMPurify from "dompurify";
import { motion } from "framer-motion";
import useIsMobile from "./useIsMobile";
import { Config } from "../../config";
import SafeHTML from "./SafeHTML";
import CookiesConsent from "./CookiesConsent";
import ControlPointIcon from "@mui/icons-material/ControlPoint";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import EastIcon from "@mui/icons-material/East";
import ContactForm from "./ContactForm";
import { useParams } from "react-router-dom";
import { Checkbox } from "@mui/material";

const MainChatView = ({
  sessionData,
  brandDetails,
  isEndedSession,
  onDisabledEndSessionBtn,
}: {
  sessionData: any;
  brandDetails: any;
  isEndedSession: boolean;
  onDisabledEndSessionBtn: (status: boolean) => void;
}) => {
  const config = new Config();
  const { version, source, uniqueId } = useParams();
  const isMobile = useIsMobile();
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [sessionId, setSessionId] = useState<string>("");
  const [endSession, setEndSession] = useState<boolean>(false);
  const [isChatStarted, setIsChatStarted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [chatText, setChatText] = useState("");
  const [chatConversationList, setChatConversationList] = useState<any[]>([]);
  const [selectedActionsMap, setSelectedActionsMap] = useState<
    Record<string, string[]>
  >({});
  const [visibleJobDescription, setVisibleJobDescription] = useState("");
  const [isContactFormVisible, setIsContactFormVisible] = useState(false);

  const messageVariants = {
    hidden: (isAgent: boolean) => ({
      opacity: 0,
      x: isAgent ? -20 : 20, // Slight shift based on sender
    }),
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  const errorMessage = DOMPurify.sanitize(`
    Something went wrong,&nbsp;
    <a href="javascript:void(0)" class="retry-link" style="color: #007bff; cursor: pointer; text-decoration: underline;">
      Please try again
    </a>.
  `);

  useEffect(() => {
    const retryLinks = document.querySelectorAll(".retry-link");

    const handleRetryClick = () => {
      const lastUserMessage = [...chatConversationList]
        .reverse()
        .find((msg) => msg.sender === "candidate")?.text;

      if (lastUserMessage) {
        handleActionClick(lastUserMessage);
      }
    };

    retryLinks.forEach((link) =>
      link.addEventListener("click", handleRetryClick),
    );

    return () => {
      retryLinks.forEach((link) =>
        link.removeEventListener("click", handleRetryClick),
      );
    };
  }, [chatConversationList]);

  useEffect(() => {
    handleChatInteraction(sessionData.objectData, true);
  }, []);

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
    onDisabledEndSessionBtn(isLoading);
  }, [isLoading]);

  useEffect(() => {
    if (endSession || isEndedSession) {
      handleEndSession();
    }
  }, [endSession, isEndedSession]);

  const handleChatInteraction = async (
    payload: any,
    initiate: boolean = false,
  ) => {
    setIsLoading(true);
    try {
      const response = await startAgentChat(payload, initiate, version);
      if (response?.message) {
        addChatMessage(response.message, "agent", response);
      } else {
        addChatMessage(errorMessage, "agent");
      }
      setEndSession(response?.end_session);
      setIsChatStarted(!initiate && response?.actions?.length <= 0);
      if (initiate) setSessionId(response.request_id);
    } catch (error) {
      console.error(`Failed to process chat:`, error);
      addChatMessage(errorMessage, "agent");
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 100);
    }
  };

  const handleEndSession = async () => {
    if (sessionStorage.getItem("sessionEnded") === "true") return;
    const payload = {
      request_id: sessionId,
    };
    removeOptions();
    //setIsLoading(true);
    try {
      const response = await endAgentChat(payload, version);
      if (response.message) {
        // addChatMessage(response.message, "agent");
        sessionStorage.setItem("sessionEnded", "true");
      } else {
        //addChatMessage(errorMessage, "agent");
      }
    } catch (error) {
      console.error(`Failed to process chat:`, error);
      //addChatMessage(errorMessage, "agent");
    } finally {
      //setIsLoading(false);
    }
  };

  const addChatMessage = (
    message: string | JSX.Element,
    sender: string,
    options?: any,
  ) => {
    //  const relatedOptions = {
    //     ...(options || {}),
    //     related_options: {
    //       ...(options?.related_options || {}),
    //       type: "form",
    //     },
    //   };

    const newMessage = {
      id: Date.now() + Math.random(),
      sender: sender, // "agent" or "candidate"
      text: message,
      options: options?.actions,
      actionType: options?.actionType || "single",
      relatedOptions: options?.related_options,
    };

    setChatConversationList((prevMessages) => [...prevMessages, newMessage]);
  };

  const handleActionTypesClick = (
    chatId: number,
    action: string,
    type: string = "single",
  ) => {
    const actionType = type.toLowerCase();

    if (actionType === "single") {
      handleActionClick(action);
      return;
    }

    if (actionType === "multi") {
      setSelectedActionsMap((prev) => {
        const current = prev[chatId] || [];

        return {
          ...prev,
          [chatId]: current.includes(action)
            ? current.filter((item) => item !== action)
            : [...current, action],
        };
      });
      return;
    }

    if (actionType === "upload") {
      handleUploadCV();
    }
  };

  const handleUploadCV = () => {
    console.log("testupload");
  };

  const handleActionClick = (title: string) => {
    removeOptions();
    addChatMessage(title, "candidate");
    startChatConversation(title);
    setChatText("");
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && chatText.trim()) {
      handleActionClick(chatText);
    }
  };

  const startChatConversation = (message: string) => {
    const payload = {
      request_id: sessionId,
      message: message,
      sender_type: "candidate",
    };
    handleChatInteraction(payload);
  };

  const removeOptions = () => {
    const updatedList = chatConversationList
      .filter((msg) => !msg.text.includes("retry-link")) // Remove error messages
      .map((chat) => ({
        ...chat,
        options: undefined,
        relatedOptions: undefined,
      }));
    setChatConversationList(updatedList);
  };

  useEffect(() => {
    if (!sessionId) return;

    const handleBeforeUnload = () => {
      fetch(`${config.settings.AgentApiUrl}agent_end/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ request_id: sessionId }),
        keepalive: true,
      });
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [sessionId]);

  const handleViewDescription = (option: any) => {
    if (!option?.description) return;
    if (visibleJobDescription === option.title.trim()) {
      setVisibleJobDescription("");
      return;
    }
    setVisibleJobDescription(option.title.trim());
  };

  return (
    <div className="h-full">
      <div
        ref={chatContainerRef}
        className={`w-full overflow-y-auto overflow-x-hidden relative ${isMobile ? "p-4" : "p-6"}`}
        style={{
          height: `calc(100% - ${isChatStarted ? (isMobile ? "80px" : "112px") : "0px"})`,
        }}
      >
        {chatConversationList &&
          chatConversationList.map((chat: any, index: number) => {
            const selectedActions = selectedActionsMap[chat.id] || [];

            return (
              <motion.div
                key={`main-${chat.id}-${index}`}
                initial="hidden"
                animate="visible"
                variants={messageVariants}
                custom={chat.sender === "agent"}
              >
                <div
                  className={`flex ${chat.sender === "agent" ? "justify-start" : "justify-end"} mb-4`}
                >
                  <SafeHTML
                    html={chat.text}
                    className={`${chat.sender === "agent" ? "agent-msg" : "user-msg"} ${isMobile ? "p-2" : "p-4 text-[16px]"} prose`}
                    style={{
                      backgroundColor:
                        chat.sender === "agent"
                          ? brandDetails.bubbleColorReceived
                          : brandDetails.bubbleColorSent,
                      color:
                        chat.sender === "agent"
                          ? brandDetails.receivedMsgTextColor
                          : brandDetails.sentMsgTextColor,
                    }}
                  />
                </div>

                {/* Related options */}
                {chat.relatedOptions?.items?.length > 0 && (
                  <div className="flex justify-start mb-4">
                    {chat.relatedOptions.type?.toLowerCase() === "form" ? (
                      <div
                        className={`agent-msg rounded-none w-full overflow-hidden primary-border-color ${isMobile ? "text-[14px]" : "text-[16px]"}`}
                        style={{
                          backgroundColor: brandDetails.bubbleColorReceived,
                          color: brandDetails.receivedMsgTextColor,
                        }}
                      >
                        <div
                          className={`flex flex-col gap-1 primary-bg-color text-white ${isMobile ? "p-2" : "p-4"}`}
                        >
                          {chat.relatedOptions?.title && (
                            <div className={`font-semibold leading-6 truncate`}>
                              {chat.relatedOptions.title}
                            </div>
                          )}
                          {chat.relatedOptions?.subTitle && (
                            <div
                              className={`leading-[17px] text-[12px] truncate`}
                            >
                              {chat.relatedOptions.subTitle}
                            </div>
                          )}
                        </div>
                        <div className={`${isMobile ? "p-2" : "p-4"}`}>
                          {chat.relatedOptions.extraInfo && (
                            <div className="flex flex-col gap-1 mb-4">
                              {chat.relatedOptions.extraInfo?.title && (
                                <div
                                  className={`font-semibold leading-6 truncate`}
                                >
                                  {chat.relatedOptions.extraInfo.title}
                                </div>
                              )}
                              {chat.relatedOptions.extraInfo?.description && (
                                <div
                                  className={`leading-[17px] text-[12px] truncate`}
                                >
                                  {chat.relatedOptions.extraInfo.description}
                                </div>
                              )}
                            </div>
                          )}
                          {chat.relatedOptions.items.map(
                            (option: any, index: number) => (
                              <div
                                className={`px-4 py-2 bg-white rounded-[12px] cursor-pointer hover:shadow ${chat.relatedOptions.items.length - 1 !== index ? (isMobile ? "mb-2" : "mb-4") : ""}`}
                                key={`relatedOptions-${chat.id}-${index}`}
                                onClick={() => handleViewDescription(option)}
                              >
                                <div className="flex items-center justify-between gap-2 w-full">
                                  <div className="flex flex-col gap-1 w-[90%]">
                                    {option?.title && (
                                      <div
                                        className={`font-semibold leading-6 truncate`}
                                      >
                                        {option.title}
                                      </div>
                                    )}
                                    {option?.subTitle && (
                                      <div
                                        className={`leading-[17px] text-[12px] truncate`}
                                      >
                                        {option.subTitle}
                                      </div>
                                    )}
                                  </div>
                                  {option.description && (
                                    <>
                                      {visibleJobDescription ===
                                      option.title.trim() ? (
                                        <RemoveCircleOutlineIcon
                                          style={{
                                            color: brandDetails.chatIconColor,
                                            fontSize: isMobile ? 15 : 20,
                                          }}
                                        />
                                      ) : (
                                        <ControlPointIcon
                                          style={{
                                            color: brandDetails.chatIconColor,
                                            fontSize: isMobile ? 15 : 20,
                                          }}
                                        />
                                      )}
                                    </>
                                  )}
                                </div>

                                {visibleJobDescription ===
                                  option.title.trim() &&
                                  option.description && (
                                    <>
                                      <hr className="my-2" />
                                      <SafeHTML
                                        html={option.description}
                                        className={`leading-[17px] text-[12px]`}
                                      />
                                    </>
                                  )}
                              </div>
                            ),
                          )}
                          {/* Apply Button */}
                          {!isContactFormVisible && (
                            <div className="flex justify-start mt-4">
                              <Button
                                variant="contained"
                                onClick={() => setIsContactFormVisible(true)}
                              >
                                Apply
                              </Button>
                            </div>
                          )}

                          {/* Contact Form */}
                          {isContactFormVisible && (
                            <div className="flex justify-start mt-4">
                              <ContactForm />
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div
                        className={`agent-msg w-full ${isMobile ? "text-[14px]" : "text-[16px]"}`}
                        style={{
                          backgroundColor: brandDetails.bubbleColorReceived,
                          color: brandDetails.receivedMsgTextColor,
                        }}
                      >
                        <div className={`${isMobile ? "p-2" : "p-4"}`}>
                          <div className="flex flex-col gap-1 mb-4">
                            {chat.relatedOptions?.title && (
                              <div
                                className={`font-semibold leading-6 truncate`}
                              >
                                {chat.relatedOptions.title}
                              </div>
                            )}
                            {chat.relatedOptions?.subTitle && (
                              <div
                                className={`leading-[17px] text-[12px] truncate`}
                              >
                                {chat.relatedOptions.subTitle}
                              </div>
                            )}
                          </div>
                          {chat.relatedOptions.items.map(
                            (option: any, index: number) => (
                              <div
                                className={`flex items-center justify-between relative overflow-hidden px-4 py-2 bg-white rounded-[12px] cursor-pointer hover:shadow ${chat.relatedOptions.items.length - 1 !== index ? (isMobile ? "mb-2" : "mb-4") : ""}`}
                                key={`relatedOptions-${chat.id}-${index}`}
                                onClick={() => handleActionClick(option.title)}
                              >
                                <div className="flex flex-col gap-1 w-[90%]">
                                  {option?.title && (
                                    <div
                                      className={`font-semibold leading-6 truncate`}
                                    >
                                      {option.title}
                                    </div>
                                  )}
                                  {option?.subTitle && (
                                    <div
                                      className={`leading-[17px] text-[14px] truncate`}
                                    >
                                      {option.subTitle}
                                    </div>
                                  )}
                                </div>

                                {/* Arrow Icon */}
                                {chat.relatedOptions.type === "level-1" && (
                                  <div
                                    style={{
                                      backgroundColor:
                                        brandDetails.chatIconColor,
                                    }}
                                    className={`flex items-center justify-center absolute right-0 bottom-0 rounded-[2px] ${isMobile ? "w-[24px] h-[24px]" : "w-[32px] h-[32px]"}`}
                                  >
                                    <EastIcon
                                      style={{
                                        color: "#fff",
                                        fontSize: isMobile ? 15 : 20,
                                      }}
                                    />
                                  </div>
                                )}

                                {/* Plus Icon */}
                                {chat.relatedOptions.type === "level-2" && (
                                  <ControlPointIcon
                                    style={{
                                      color: brandDetails.chatIconColor,
                                      fontSize: isMobile ? 15 : 20,
                                    }}
                                  />
                                )}
                              </div>
                            ),
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Options */}
                {chat.options && chat.options.length > 0 && (
                  <>
                    <hr className="mb-4"></hr>
                    <div
                      className={`mb-4 flex justify-start flex-wrap max-w-[90%] ${isMobile ? "gap-2" : "gap-4"}`}
                    >
                      {chat.actionType === "upload" ? (
                        <Button
                          variant="contained"
                          onClick={() =>
                            handleActionTypesClick(chat.id, "", chat.actionType)
                          }
                        >
                          Upload CV
                        </Button>
                      ) : (
                        chat.options.map((option: any, index: number) => (
                          <Button
                            key={`options-${chat.id}-${index}`}
                            variant="outlined"
                            className="truncate"
                            title={option}
                            sx={{
                              borderRadius: "9999px",
                              height: isMobile ? "32px" : "40px",
                              textTransform: "none",
                              backgroundColor: brandDetails.chatButtonColor,
                              borderColor: brandDetails.chatButtonStrokeColor,
                              color: brandDetails.chatButtonTextColor,
                              "&:hover": {
                                opacity: 0.8,
                                backgroundColor: brandDetails.chatButtonColor,
                                borderColor: brandDetails.chatButtonStrokeColor,
                                color: brandDetails.chatButtonTextColor,
                              },
                            }}
                            onClick={() =>
                              handleActionTypesClick(
                                chat.id,
                                option,
                                chat.actionType,
                              )
                            }
                          >
                            <div className="flex items-center gap-2 truncate">
                              {chat.actionType?.toLowerCase() === "multi" && (
                                  <Checkbox
                                    sx={{
                                      color: brandDetails.chatButtonTextColor,
                                      "&.Mui-checked": {
                                        color:
                                          brandDetails.chatButtonStrokeColor,
                                      },
                                    }}
                                    checked={(
                                      selectedActionsMap[chat.id] || []
                                    ).includes(option)}
                                    onChange={() =>
                                      handleActionTypesClick(
                                        chat.id,
                                        option,
                                        chat.actionType,
                                      )
                                    }
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                )}

                              <span className="truncate">{option}</span>
                            </div>
                          </Button>
                        ))
                      )}
                      {chat.actionType?.toLowerCase() === "multi" &&
                        (selectedActionsMap[chat.id]?.length || 0) > 0 && (
                          <Button
                            variant="contained"
                            sx={{
                              borderRadius: "9999px",
                              minWidth: isMobile ? "36px" : "48px",
                              height: isMobile ? "32px" : "40px",
                              backgroundColor: brandDetails.chatButtonColor,
                              color: brandDetails.chatButtonTextColor,
                              "&:hover": {
                                opacity: 0.8,
                                backgroundColor: brandDetails.chatButtonColor,
                                borderColor: brandDetails.chatButtonStrokeColor,
                              },
                            }}
                            onClick={() => {
                              handleActionClick(selectedActions.join(", "));
                              setSelectedActionsMap((prev) => ({
                                ...prev,
                                [chat.id]: [],
                              }));
                            }}
                          >
                            <svg
                              width={isMobile ? 12 : 18}
                              height={isMobile ? 12 : 18}
                              viewBox="0 0 12 12"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M0.296875 0.750977C0.296875 0.300977 0.671875 0.000976562 1.04688 0.000976562C1.19688 0.000976562 1.27188 0.000976562 1.42188 0.0759766L11.2469 5.32598C11.6219 5.55098 11.7719 6.00098 11.5469 6.37598C11.4719 6.52598 11.3969 6.60098 11.2469 6.67598L1.42188 11.926C1.04688 12.151 0.596875 12.001 0.371875 11.626C0.296875 11.551 0.296875 11.401 0.296875 11.251V7.80098C0.296875 7.42598 0.521875 7.12598 0.896875 7.05098L6.44688 6.00098L0.896875 4.95098C0.521875 4.87598 0.296875 4.57598 0.296875 4.20098V0.750977Z"
                                fill={brandDetails.chatButtonStrokeColor}
                              />
                            </svg>
                          </Button>
                        )}
                    </div>
                  </>
                )}
              </motion.div>
            );
          })}

        {/* Agent typing animation */}
        {isLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex justify-start">
              <div
                className={`agent-msg mb-4 ${isMobile ? "p-2" : "p-4 text-[16px]"}`}
                style={{
                  backgroundColor: brandDetails.bubbleColorReceived,
                  color: brandDetails.receivedMsgTextColor,
                }}
              >
                <TypingLoader />
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Chat typing area */}
      {isChatStarted && (
        <div
          className={`flex items-center gap-2 ${isMobile ? "p-4" : "p-6 text-[16px]"}`}
        >
          <div
            className={`${isLoading || endSession ? "opacity-25 pointer-events-none" : ""} bg-white border-Gray5 flex items-center rounded-md w-full ${isMobile ? "px-2 py-[5px]" : "px-4 py-2"}`}
          >
            <input
              type="text"
              placeholder="Write your message here"
              value={chatText}
              onChange={(e) => setChatText(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex-grow px-2 text-gray3 focus:outline-none"
            />
            <Button
              sx={{
                borderRadius: "6px",
                minWidth: isMobile ? "36px" : "48px",
                minHeight: isMobile ? "36px" : "48px",
                padding: isMobile ? "12px" : "10px",
                backgroundColor: brandDetails.sendButtonColor,
                "&:hover": {
                  backgroundColor: brandDetails.sendButtonColor,
                  opacity: 0.8,
                },
              }}
              variant="contained"
              onClick={() => handleActionClick(chatText)}
              disabled={!chatText}
            >
              <svg
                width={isMobile ? 12 : 26}
                height={isMobile ? 12 : 26}
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g>
                  <path
                    d="M0.296875 0.750977C0.296875 0.300977 0.671875 0.000976562 1.04688 0.000976562C1.19688 0.000976562 1.27188 0.000976562 1.42188 0.0759766L11.2469 5.32598C11.6219 5.55098 11.7719 6.00098 11.5469 6.37598C11.4719 6.52598 11.3969 6.60098 11.2469 6.67598L1.42188 11.926C1.04688 12.151 0.596875 12.001 0.371875 11.626C0.296875 11.551 0.296875 11.401 0.296875 11.251V7.80098C0.296875 7.42598 0.521875 7.12598 0.896875 7.05098L6.44688 6.00098L0.896875 4.95098C0.521875 4.87598 0.296875 4.57598 0.296875 4.20098V0.750977Z"
                    fill="white"
                  />
                </g>
              </svg>
            </Button>
          </div>
        </div>
      )}
      {sessionId && (
        <CookiesConsent
          consentResponse={sessionData?.objectData?.consent}
          brandDetails={brandDetails}
          sessionId={sessionId}
        />
      )}
    </div>
  );
};

export default MainChatView;
